import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { BellRing } from 'lucide-react';
import type { EventSubscriptionToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Pill } from '~/components/pill';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useEventSubscriptionsList, useEventSubscriptionMutations } from '~/hooks/use-event-subscriptions';

/** Event subscriptions surface — list with edit (email + disabled) and delete; subscriptions are created by the storefront. */
export function EventSubscriptionsGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useEventSubscriptionsList();
	const { remove } = useEventSubscriptionMutations();

	const [deleteRow, setDeleteRow] = useState<EventSubscriptionToken | null>(null);

	const columns = useMemo<SystemGridColumn<EventSubscriptionToken>[]>(
		() => [
			{
				key: 'type',
				header: t('system.eventSubscriptions.columns.type'),
				width: '220px',
				sortValue: s => s.EventSubscriptionsType ?? '',
				exportValue: s => s.EventSubscriptionsType ?? '',
				render: s => <span className="truncate text-sm font-medium text-foreground">{s.EventSubscriptionsType || '—'}</span>
			},
			{
				key: 'email',
				header: t('system.eventSubscriptions.columns.email'),
				width: 'minmax(0,1fr)',
				sortValue: s => s.eml ?? '',
				exportValue: s => s.eml ?? '',
				render: s => <span className="truncate text-sm text-foreground-muted">{s.eml || '—'}</span>
			},
			{
				key: 'payload',
				header: t('system.eventSubscriptions.columns.payload'),
				width: '200px',
				exportValue: s => s.Payload ?? '',
				render: s => (
					<span className="truncate font-mono text-[11px] text-foreground-subtle" title={s.Payload ?? ''}>
						{s.Payload || '—'}
					</span>
				)
			},
			{
				key: 'disabled',
				header: t('system.eventSubscriptions.columns.status'),
				width: '110px',
				align: 'center',
				sortValue: s => (s.Disabled ? 1 : 0),
				exportValue: s => (s.Disabled ? t('system.eventSubscriptions.disabled') : t('system.eventSubscriptions.active')),
				render: s =>
					s.Disabled ? (
						<Pill variant="neutral">{t('system.eventSubscriptions.disabled')}</Pill>
					) : (
						<Pill variant="success">{t('system.eventSubscriptions.active')}</Pill>
					)
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<EventSubscriptionToken>
				gridId="event-subscriptions"
				title={t('system.eventSubscriptions.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={s => s.id ?? `${s.EventSubscriptionsType}-${s.eml}`}
				columns={columns}
				searchPredicate={(s, q) => `${s.eml ?? ''} ${s.EventSubscriptionsType ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'type', dir: 'asc' }}
				emptyIcon={<BellRing size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				onEdit={s => navigate(`/system/event-subscriptions/${s.id}`)}
				onDelete={setDeleteRow}
				exportFilename="event-subscriptions"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.eventSubscriptions.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.eventSubscriptions.deleteBody', { name: deleteRow?.eml ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
